import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import toast from 'react-hot-toast';
import { Scan, Smile, Grid3x3, KeyRound } from 'lucide-react'; 

// Import all credential modals
import AddFingerprintModal from '../devices/AddFingerprintModal'; 
import AddFaceModal from '../devices/AddFaceModal'; 
import AddPasscodeModal from '../devices/AddPasscodeModal'; 

const EditEmployeeModal = ({
  user,
  person,
  onClose,
  onEmployeeUpdated,
  propertyId,
}) => {
  const [firstName, setFirstName] = useState(person.first_name || '');
  const [lastName, setLastName] = useState(person.last_name || '');
  const [email, setEmail] = useState(person.email || '');
  const [phone, setPhone] = useState(person.phone || '');
  const [role, setRole] = useState(person.role || 'Housekeeper');
  const [isActive, setIsActive] = useState(!!person.is_active);
  
  // Quick Access Code State
  const [quickAccessCode, setQuickAccessCode] = useState(person.quick_access_code || '');
  
  const [isLoading, setIsLoading] = useState(false);

  // Biometric & Security State
  const [isAddFingerModalOpen, setIsAddFingerModalOpen] = useState(false);
  const [isAddFaceModalOpen, setIsAddFaceModalOpen] = useState(false); 
  const [isSetPasscodeOpen, setIsSetPasscodeOpen] = useState(false); 

  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');

  // --- ROLE HIERARCHY LOGIC (Inside Modal) ---
  // This controls if you can change the Role/Status dropdowns
  const roleValues = {
      'SuperAdmin': 100,
      'Owner': 50,
      'Manager': 40,
      'Accountant': 30,
      'Housekeeper': 10
  };

  const myLevel = roleValues[user.role] || 0;
  const targetLevel = roleValues[person.role] || 0;

  // Rules:
  // 1. SuperAdmin can edit everything.
  // 2. You can edit sensitive fields if your rank is HIGHER than the target.
  const canEditSensitiveFields = 
      user.role === 'SuperAdmin' || 
      (myLevel > targetLevel && (user.id || user.userId) !== person.id);

  // Helper: Determine capabilities based on model name
  const getDeviceCapabilities = (device) => {
      const defaults = { face: false, fp: true, passcode: true };
      if (!device || !device.product_name) return defaults; 
      
      const model = device.product_name.toUpperCase();
      if (['F162', 'FACE', 'MB10', 'MB20', 'MB160', 'F500', 'V5L'].some(k => model.includes(k))) {
          return { ...defaults, face: true };
      }
      return defaults;
  };

  useEffect(() => {
    // Only load devices if we are editing a Housekeeper
    if (role === 'Housekeeper') {
      const fetchDevices = async () => {
        try {
          const deviceData = await api.getDevices({ propertyId });
          setDevices(deviceData || []);
          if (deviceData && deviceData.length > 0) {
            setSelectedDeviceId(deviceData[0].id);
          }
        } catch (error) {
          toast.error("Could not load devices for this property.");
        }
      };
      fetchDevices();
    }
  }, [propertyId, role]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (role === 'Housekeeper' && quickAccessCode && !/^\d{4}$/.test(quickAccessCode)) {
        toast.error("Quick Access PIN must be exactly 4 digits.");
        setIsLoading(false);
        return;
    }

    const updatedData = { 
        first_name: firstName, 
        last_name: lastName, 
        email, 
        phone, 
        role, 
        is_active: isActive, 
        propertyId: propertyId,
        quick_access_code: role === 'Housekeeper' ? quickAccessCode : null 
    };

    try {
      await api.updateWorkforcePerson(person.id, updatedData);
      toast.success('User updated successfully!');
      onEmployeeUpdated();
      onClose();
    } catch (err) { toast.error(err.message || 'Failed to update user.'); } 
    finally { setIsLoading(false); }
  };

  const selectedDeviceForAction = devices.find(d => d.id === parseInt(selectedDeviceId));
  const deviceCaps = getDeviceCapabilities(selectedDeviceForAction);

  const getCardStyle = (disabled) => ({
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '15px', border: '1px solid #e2e8f0', borderRadius: '8px',
      background: disabled ? '#f8fafc' : '#fff',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s',
      opacity: disabled ? 0.6 : 1,
      minHeight: '110px',
      width: '100%'
  });

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content">
          <h2>Edit {person.first_name} {person.last_name}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group"><label className="form-label">First Name</label><input type="text" className="form-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} required /></div>
              <div className="form-group"><label className="form-label">Last Name</label><input type="text" className="form-input" value={lastName} onChange={(e) => setLastName(e.target.value)} required /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Email Address</label><input type="email" className="form-input" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
              <div className="form-group"><label className="form-label">Phone Number</label><input type="tel" className="form-input" value={phone} onChange={(e) => setPhone(e.target.value)} required /></div>
            </div>
            
            <div className="form-row">
                <div className="form-group">
                    <label className="form-label">Role</label>
                    <select 
                        className="form-input" 
                        value={role} 
                        onChange={(e) => setRole(e.target.value)}
                        disabled={!canEditSensitiveFields} 
                        title={!canEditSensitiveFields ? "You cannot change the role of this user." : ""}
                    >
                        <option value="SuperAdmin">SuperAdmin</option>
                        <option value="Owner">Owner</option>
                        <option value="Manager">Manager</option>
                        <option value="Accountant">Accountant</option>
                        <option value="Housekeeper">Housekeeper</option>
                    </select>
                </div>
                <div className="form-group">
                    <label className="form-label">Status</label>
                    <div className="toggle-switch-container">
                        <label className="toggle-switch">
                            <input 
                                type="checkbox" 
                                checked={isActive} 
                                onChange={() => setIsActive(!isActive)} 
                                disabled={!canEditSensitiveFields}
                            />
                            <span className="slider"></span>
                        </label>
                        <span className="toggle-label">{isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                </div>
            </div>

            {/* --- KIOSK ACCESS SECTION --- */}
            {role === 'Housekeeper' && (
                <div className="form-section" style={{marginTop:'15px', padding:'15px', background:'#f8fafc', borderRadius:'8px', border:'1px solid #e2e8f0'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px'}}>
                        <KeyRound size={18} color="#0f172a"/>
                        <h3 className="form-section-title" style={{margin:0}}>Portal Access</h3>
                    </div>
                    
                    <div className="form-group">
                        <label className="form-label" style={{display:'flex', justifyContent:'space-between'}}>
                            Quick Access PIN
                            <span style={{fontWeight:400, color:'#64748b', fontSize:'0.8rem'}}>4 Digits</span>
                        </label>
                        <input 
                            type="text" 
                            className="form-input" 
                            value={quickAccessCode} 
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                                setQuickAccessCode(val);
                            }} 
                            placeholder="Enter 4-digit PIN"
                            style={{letterSpacing: '2px', fontWeight: 'bold'}}
                        />
                        <p style={{fontSize:'0.75rem', color:'#64748b', marginTop:'6px'}}>
                            Allows login to the housekeeping portal via phone number.
                        </p>
                    </div>
                </div>
            )}

            {/* --- BIOMETRIC SECTION --- */}
            {role === 'Housekeeper' && (
                <div className="form-section">
                    <h3 className="form-section-title">Time Clock Device</h3>
                    <div className="form-group">
                        <label className="form-label">Manage on Device</label>
                        <select
                            className="form-input"
                            value={selectedDeviceId}
                            onChange={(e) => setSelectedDeviceId(e.target.value)}
                            disabled={devices.length === 0}
                        >
                            {devices.length > 0 ? (
                                devices.map(device => (
                                    <option key={device.id} value={device.id}>
                                        {device.name} ({device.product_name || 'Standard'})
                                    </option>
                                ))
                            ) : (
                                <option>No devices found</option>
                            )}
                        </select>
                    </div>
                    
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: deviceCaps.face ? '1fr 1fr 1fr' : '1fr 1fr', 
                        gap: '15px', 
                        marginTop: '15px' 
                    }}>
                        {deviceCaps.fp && (
                            <button
                                type="button"
                                onClick={() => !(!selectedDeviceId) && setIsAddFingerModalOpen(true)}
                                disabled={!selectedDeviceId}
                                style={getCardStyle(!selectedDeviceId)}
                                onMouseEnter={e => { if(selectedDeviceId) { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.background = '#eff6ff'; }}}
                                onMouseLeave={e => { if(selectedDeviceId) { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#fff'; }}}
                            >
                                <div style={{ background: '#dbeafe', padding: '10px', borderRadius: '50%', marginBottom: '8px', color: '#2563eb' }}>
                                    <Scan size={20} />
                                </div>
                                <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>Fingerprint</span>
                            </button>
                        )}
                        
                        {deviceCaps.face && (
                            <button
                                type="button"
                                onClick={() => !(!selectedDeviceId) && setIsAddFaceModalOpen(true)}
                                disabled={!selectedDeviceId}
                                style={getCardStyle(!selectedDeviceId)}
                                onMouseEnter={e => { if(selectedDeviceId) { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.background = '#eff6ff'; }}}
                                onMouseLeave={e => { if(selectedDeviceId) { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#fff'; }}}
                            >
                                <div style={{ background: '#dbeafe', padding: '10px', borderRadius: '50%', marginBottom: '8px', color: '#2563eb' }}>
                                    <Smile size={20} />
                                </div>
                                <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>Face ID</span>
                            </button>
                        )}

                        {deviceCaps.passcode && (
                            <button
                                type="button"
                                onClick={() => !(!selectedDeviceId) && setIsSetPasscodeOpen(true)}
                                disabled={!selectedDeviceId}
                                style={getCardStyle(!selectedDeviceId)}
                                onMouseEnter={e => { if(selectedDeviceId) { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.background = '#eff6ff'; }}}
                                onMouseLeave={e => { if(selectedDeviceId) { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#fff'; }}}
                            >
                                <div style={{ background: '#dbeafe', padding: '10px', borderRadius: '50%', marginBottom: '8px', color: '#2563eb' }}>
                                    <Grid3x3 size={20} />
                                </div>
                                <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>Passcode</span>
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className="modal-actions">
              <button type="button" className="button button-secondary" onClick={onClose} disabled={isLoading}>Cancel</button>
              <button type="submit" className="button action-button" disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* --- EXTERNAL MODALS --- */}
      {isAddFingerModalOpen && <AddFingerprintModal device={selectedDeviceForAction} person={person} onClose={() => setIsAddFingerModalOpen(false)} />}
      {isAddFaceModalOpen && <AddFaceModal device={selectedDeviceForAction} person={person} onClose={() => setIsAddFaceModalOpen(false)} />}
      {isSetPasscodeOpen && <AddPasscodeModal device={selectedDeviceForAction} person={person} onClose={() => setIsSetPasscodeOpen(false)} />}
    </>
  );
};

export default EditEmployeeModal;